if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        if (!this.form) return;

        this.onSubmitHandlerBound = this.onSubmitHandler.bind(this);
        this.onClickSubmitFallbackBound = this.onClickSubmitFallback.bind(this);
        // Guards duplicate requests when multiple handlers fire for one click.
        this.isSubmitting = false;

        if (this.form.querySelector("[name=id]")) {
          this.form.querySelector("[name=id]").disabled = false;
        }

        // Keep the native submit listener for keyboard submit and requestSubmit().
        this.form.addEventListener("submit", this.onSubmitHandlerBound);
        // Capture submit-button clicks at component level to normalize behavior.
        this.addEventListener("click", this.onClickSubmitFallbackBound, true);

        this.submitButton = this.querySelector('[type="submit"]');
      }

      connectedCallback() {
        if (!this.dataset.orderLimits) return;
        window.orderQuantityLimits.register(JSON.parse(this.dataset.orderLimits));
        this.onOrderQuantityUpdateBound = () => this.updateOrderQuantityState();
        document.addEventListener("order-quantity:updated", this.onOrderQuantityUpdateBound);
        this.form.addEventListener("change", this.onOrderQuantityUpdateBound);
        this.updateOrderQuantityState();
      }

      updateOrderQuantityState() {
        if (!this.dataset.orderLimits) return;
        const variantId = this.form.querySelector("[name=id]").value;
        const sellingPlan = this.form.querySelector('[name="selling_plan"]')?.value;
        const quantityInput = this.form.querySelector('[name="quantity"]');
        const state = window.orderQuantityLimits.evaluateAdd(
          variantId,
          quantityInput ? Number(quantityInput.value) : 1,
          Boolean(sellingPlan),
        );
        this.quantityBlocked = state.blocked;
        if (quantityInput) {
          if (Number.isFinite(state.remaining)) quantityInput.max = String(state.remaining);
          else quantityInput.removeAttribute("max");
        }

        // Share the inline alert so purchase-option changes replace or clear either limit.
        if (state.blocked) {
          this.handleErrorMessage(state.message);
          this.cartLimitError = true;
        } else if (this.cartLimitError) {
          this.handleErrorMessage();
          this.cartLimitError = false;
        }

        this.form.querySelectorAll("[data-variant-available]").forEach((button) => {
          // Keep availability separate so one-time selection cannot enable sold-out variants.
          button.disabled =
            button.dataset.variantAvailable !== "true" ||
            this.quantityBlocked;
        });
      }

      disconnectedCallback() {
        if (this.onOrderQuantityUpdateBound) {
          document.removeEventListener("order-quantity:updated", this.onOrderQuantityUpdateBound);
        }
        this.form?.removeEventListener(
          "change",
          this.onOrderQuantityUpdateBound,
        );
        this.form?.removeEventListener("submit", this.onSubmitHandlerBound);
        this.removeEventListener(
          "click",
          this.onClickSubmitFallbackBound,
          true,
        );
      }

      onClickSubmitFallback(evt) {
        const submitter = evt.target.closest(
          'button[type="submit"], input[type="submit"]',
        );

        if (!submitter || submitter.form !== this.form) return;

        // Route click-initiated submits through one JS path.
        // This prevents duplicate add-to-cart calls from competing listeners.
        evt.preventDefault();
        evt.stopPropagation();

        if (this.isSubmitting) return;

        this.onSubmitHandler({
          preventDefault() {},
          submitter,
        });
      }

      onSubmitHandler(evt) {
        console.log("product-form onSubmitHandler");

        evt.preventDefault();

        this.submitButton =
          evt.submitter ||
          this.form.querySelector(
            'button[type="submit"], input[type="submit"]',
          );

        if (!this.submitButton) return;

        // Recheck at submission time so keyboard or programmatic submits cannot bypass the guard.
        this.updateOrderQuantityState();
        if (this.submitButton.disabled || this.quantityBlocked) return;

        // Ignore re-entrant submits while a cart add request is in flight.
        if (this.isSubmitting) return;
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;
        this.isSubmitting = true;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);

        // Only include the subscription terms property if a selling plan is selected.
        if (!formData.get("selling_plan")) {
          formData.delete("properties[_subscription_terms]");
        }

        // Reserve allowance across PDP/sticky/quickview while the add is in flight.
        const quantityReservation =
          this.dataset.orderLimits
            ? window.orderQuantityLimits.reserve(
                formData.get("id"),
                Number(formData.get("quantity") || 1),
                Boolean(formData.get("selling_plan")),
              )
            : null;

        // Preserve all form fields (variant, quantity, properties, selling plan).
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");

              if (!soldOutMessage) return;

              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");

              this.error = true;
              return;
            }

            this.error = false;

            if (this.dataset.orderLimits) {
              window.orderQuantityLimits.recordAdd(
                {
                  ...response,
                  id: formData.get("id"),
                  quantity: response.quantity ?? Number(formData.get("quantity") || 1),
                  selling_plan: formData.get("selling_plan") || null,
                },
              );
            }

            // -----------------------------
            // Trigger HS_SLIDE_CART_UPDATED
            // -----------------------------
            const cartRefresh = fetch(`${routes.cart_url}.js`)
              .then((res) => {
                if (!res.ok) {
                  throw new Error(`Cart refresh failed: ${res.status}`);
                }
                return res.json();
              })
              .then((cart) => {
                window.orderQuantityLimits.setCart(cart);
                if (window.HS_SLIDE_CART_UPDATED) {
                  window.HS_SLIDE_CART_UPDATED(cart);
                }
              })
              .catch((err) => console.warn("Cart refresh failed", err));

            // UI updates
            const quickViewModal = document.querySelector("quick-view-modal");
            if (quickViewModal && typeof quickViewModal.hide === "function") {
              quickViewModal.hide();
            }

            const stickyAtc = document.querySelector("#StickyAddToCart");
            if (stickyAtc && stickyAtc.classList.contains("active")) {
              stickyAtc.classList.remove("active");
            }

            return cartRefresh;
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (quantityReservation) {
              window.orderQuantityLimits.release(quantityReservation);
            }
            // Always release UI/request locks regardless of success or failure.
            this.isSubmitting = false;
            this.submitButton.classList.remove("loading");

            if (!this.error) {
              this.submitButton.removeAttribute("aria-disabled");
            }

            const spinner = this.querySelector(".loading-overlay__spinner");
            if (spinner) spinner.setAttribute("hidden", "");
          });
      }

      handleErrorMessage(errorMessage = false) {
        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
